/**
 * User Data Migration Utility
 * 
 * This utility handles the migration from old user data format (storeId) 
 * to new format (storeIds array) in localStorage.
 */

interface OldUserData {
    storeId?: string | any;
    [key: string]: any;
}

interface NewUserData {
    storeIds?: string[] | any[];
    [key: string]: any;
}

/**
 * Migrates user data from old format to new format
 * Converts single storeId to storeIds array
 */
export function migrateUserData(userData: OldUserData): NewUserData {
    if (!userData) return userData;

    // If already has storeIds, return as is
    if (userData.storeIds) {
        return userData as NewUserData;
    }

    // If has old storeId, convert to storeIds array
    if (userData.storeId) {
        const { storeId, ...rest } = userData;
        return {
            ...rest,
            storeIds: [storeId]
        };
    }

    // No store data, add empty array
    return {
        ...userData,
        storeIds: []
    };
}

/**
 * Gets user data from localStorage and migrates if needed
 */
export function getUserFromStorage(): NewUserData | null {
    try {
        const userStr = localStorage.getItem('user');
        if (!userStr) return null;

        const userData = JSON.parse(userStr);
        const migratedData = migrateUserData(userData);

        // Save migrated data back to localStorage
        if (userData.storeId && !userData.storeIds) {
            localStorage.setItem('user', JSON.stringify(migratedData));
        }

        return migratedData;
    } catch (error) {
        console.error('Error getting user from storage:', error);
        return null;
    }
}

/**
 * Gets the primary store ID for the user
 * Returns the first store in the storeIds array
 */
export function getPrimaryStoreId(user: NewUserData | null): string | null {
    if (!user || !user.storeIds || user.storeIds.length === 0) {
        return null;
    }

    const firstStore = user.storeIds[0];

    // Handle if store is an object with _id
    if (typeof firstStore === 'object' && firstStore._id) {
        return firstStore._id;
    }

    // Handle if store is just an ID string
    return firstStore;
}

/**
 * Gets all store IDs for the user
 */
export function getAllStoreIds(user: NewUserData | null): string[] {
    if (!user || !user.storeIds || user.storeIds.length === 0) {
        return [];
    }

    return user.storeIds.map(store => {
        if (typeof store === 'object' && store._id) {
            return store._id;
        }
        return store;
    });
}

/**
 * Checks if user has access to multiple stores
 */
export function hasMultipleStores(user: NewUserData | null): boolean {
    return !!(user?.storeIds && user.storeIds.length > 1);
}

/**
 * Gets store display name
 */
export function getStoreDisplayName(user: NewUserData | null): string {
    if (!user || !user.storeIds || user.storeIds.length === 0) {
        return 'Global';
    }

    const firstStore = user.storeIds[0];

    if (typeof firstStore === 'object' && firstStore.name) {
        const storeName = firstStore.name;
        const additionalCount = user.storeIds.length - 1;
        return additionalCount > 0 ? `${storeName} +${additionalCount}` : storeName;
    }

    return 'N/A';
}
